import { $ as pdfRectWidth, A as refKey, B as decodeStableIdKey, C as appearanceImpactOf, D as shiftsExistingAnnotationIndices, E as invalidatesWeakIndexRefs, F as resolveBinarySource, G as normalizePdfRect, H as isValidPageObjectNumber, I as NO_ANNOTATION_FLAGS, J as pdfQuadFromCorners, K as pdfQuadBounds, L as UNKNOWN_WEAK_ANNOTATION_STATE, M as normalizeAnnotationPatch, N as normalizeAttachmentFileSource, O as buildThreads, P as sniffBinaryMetadata, Q as pdfRectToOriginSize, R as knownWeakAnnotationState, S as encodeFieldRefKey, T as changesAnnotationList, U as DEFAULT_PDF_SAVE_MODE, V as encodeStableIdKey, W as createPageImageHandle, X as pdfRectHeight, Y as pdfRectFromOriginSize, Z as pdfRectSize, _ as foldOptionsFor, a as PDF_BITS, b as wordBefore, c as searchContentEpoch, d as buildSnippet, f as matchPageText, g as validateSearchRegex, h as validateSearchQuery, i as pdfPermissions, j as normalizeAnnotationDraft, k as classifyRelation, l as searchRectsForRange, m as matchRegex, n as collab, o as decodePdfBits, p as SEARCH_REGEX_MAX_LENGTH, q as pdfQuadCorners, r as materializePdfPermissions, s as canonicalSearchQuery, t as caps, u as SEARCH_SNIPPET_CONTEXT, v as matchLiteral, w as semanticEqual, x as decodeFieldRefKey, y as wordAt, z as revisionTokensEqual } from "./builders-C548U47M.js";
import { a as subtypeFromCode, i as PdfAnnotationSubtypeCode, n as PDF_CODE_TO_SUBTYPE, o as EngineErrorCode, r as PDF_SUBTYPE_TO_CODE, t as ANNOTATION_SUBTYPES } from "./subtype-BvAzLCGC.js";
import { a as deserializeError, i as EngineError, n as foldText, o as serializeError, r as toOriginalRange, s as _defineProperty, t as SEARCH_FOLD_VERSION } from "./fold-CB4fAOOy.js";
import { a as expandRawScope, c as parseScope, d as MissingIdentity, f as PermissionDenied, i as checkSetGroup, l as validateScopeArray, n as checkCapability, o as expandedCapabilities, r as checkCollab, s as filterMatches, t as checkAnyCapability, u as InvalidScope } from "./resolver-DiPMTfcy.js";
import { n as isAbortError, t as AbortError } from "./AbortError-CjSasIlL.js";
//#region src/promise/AbortablePromise.ts
let _Symbol$species;
_Symbol$species = Symbol.species;
/**
* Awaitable Promise subclass with abort and progress.
*
* Symbol.species returns plain Promise so chained .then/.catch/.finally
* return regular promises and never try to synthesize a new
* AbortablePromise (which would have no executor).
*
* Settlement contract:
*   - The first settlement wins. resolve/reject handed to the executor
*     are wrapped so that any subsequent call (whether by the executor,
*     by abort(), or by both racing) becomes a silent no-op.
*   - abort() rejects the public promise with AbortError immediately,
*     even if the executor never observes the AbortSignal. The executor
*     still receives the AbortSignal so it can stop real work
*     cooperatively (release a queue slot, abort fetch, post a worker
*     abort, etc.). Anything the executor does after that — including
*     a late resolve(value), a late reject(error), or a rejected inner
*     Promise inside AbortablePromise.run — is dropped silently.
*   - abort() after settlement is a no-op.
*/
var AbortablePromise = class AbortablePromise extends Promise {
	static get [_Symbol$species]() {
		return Promise;
	}
	constructor(executor) {
		const ctrl = new AbortController();
		const progressCbs = /* @__PURE__ */ new Set();
		const state = { settled: false };
		let capturedReject;
		super((resolve, reject) => {
			capturedReject = reject;
			const wrappedResolve = (value) => {
				if (state.settled) return;
				state.settled = true;
				resolve(value);
			};
			const wrappedReject = (reason) => {
				if (state.settled) return;
				state.settled = true;
				reject(reason);
			};
			const progress = (p) => {
				if (state.settled) return;
				for (const cb of progressCbs) try {
					cb(p);
				} catch {}
			};
			executor(wrappedResolve, wrappedReject, progress, ctrl.signal);
		});
		_defineProperty(this, "signal", void 0);
		_defineProperty(this, "_ctrl", void 0);
		_defineProperty(this, "_progressCbs", void 0);
		_defineProperty(this, "_state", void 0);
		_defineProperty(this, "_internalReject", void 0);
		this._ctrl = ctrl;
		this.signal = ctrl.signal;
		this._progressCbs = progressCbs;
		this._state = state;
		this._internalReject = capturedReject;
	}
	/**
	* Abort the underlying operation.
	*
	* - If the promise has already settled, this is a no-op.
	* - Otherwise the public promise rejects synchronously with an
	*   AbortError (wrapping the supplied reason if it is not already an
	*   AbortError) and the AbortSignal fires for any cooperative
	*   cancellation the executor wired up.
	*/
	abort(reason) {
		if (this._state.settled) return;
		if (this._ctrl.signal.aborted) return;
		const err = reason instanceof AbortError ? reason : new AbortError(reason);
		this._state.settled = true;
		this._ctrl.abort(err);
		this._internalReject(err);
	}
	/**
	* Subscribe to progress events. Returns an unsubscribe function.
	*/
	onProgress(cb) {
		this._progressCbs.add(cb);
		return () => {
			this._progressCbs.delete(cb);
		};
	}
	/**
	* Wrap a value in an already-resolved AbortablePromise. Mirrors Promise.resolve.
	*/
	static resolveValue(value) {
		return new AbortablePromise((resolve) => resolve(value));
	}
	/**
	* Wrap a thrown reason in an already-rejected AbortablePromise.
	*/
	static rejectReason(reason) {
		return new AbortablePromise((_resolve, reject) => reject(reason));
	}
	/**
	* Run an async function as an AbortablePromise so callers get .abort() and .signal.
	* The async function receives the AbortSignal (and a progress emitter) so it can
	* cooperate with the abort.
	*
	* If abort() fires while the inner async function is still running, the
	* public promise rejects with AbortError immediately. The inner async
	* function's eventual settlement (resolve or reject) is funneled through
	* the wrapped settlers and silently dropped — including a late rejection
	* from the underlying work, which prevents an UnhandledRejection because
	* the .then(resolve, reject) below "handles" it via the no-op.
	*/
	static run(fn) {
		return new AbortablePromise((resolve, reject, progress, signal) => {
			fn(signal, progress).then(resolve, reject);
		});
	}
};
//#endregion
//#region src/engine/document-security-state.ts
function securityStateFromHead(head) {
	return {
		encryption: {
			state: head.encryption.state,
			requiresPassword: head.encryption.requiresPassword
		},
		permissions: { ...head.permissions },
		access: {
			required: head.access.required,
			reasons: [...head.access.reasons],
			...head.access.endpoint ? { endpoint: head.access.endpoint } : {}
		}
	};
}
function permissionInfoFromProbe(info) {
	if (info.pdfPermissionsBits === null) return null;
	return {
		known: true,
		bits: info.pdfPermissionsBits,
		allAllowed: info.pdfPermissionsAllAllowed,
		openedAs: info.pdfOpenedAs,
		securityHandlerRevision: info.securityHandlerRevision
	};
}
/**
* Same as {@link permissionInfoFromProbe} but additionally populates
* `flags` (typed PdfBits view) and `advisory` (capability-shaped
* booleans for UI badges). Used by routes that have a decoded `PdfBits`
* in hand — notably `/access`, which always does.
*
* Returns `null` for the same reason as the base function — when the
* PDF hasn't been probed yet so bits are unknown.
*/
function permissionInfoWithAdvisory(info, pdfBits) {
	const base = permissionInfoFromProbe(info);
	if (!base) return null;
	return {
		...base,
		flags: pdfBits,
		advisory: advisoryFromPdfBits(pdfBits)
	};
}
/**
* Translate a {@link PdfBits} view into the capability-shaped
* {@link PdfPermissionAdvisory}. The rules here MUST mirror the bit
* combinations the scope resolver uses for `pdf.permissions`
* expansion — `doc.print.high` requires bit 12 AND bit 3, etc.
*/
function advisoryFromPdfBits(b) {
	return {
		canPrint: b.bit3,
		canPrintHigh: b.bit12 && b.bit3,
		canCopy: b.bit5,
		canAnnotate: b.bit6,
		canFillForms: b.bit6 || b.bit9,
		canModifyForms: b.bit6 && b.bit4,
		canModifyPages: b.bit4,
		canAssemble: b.bit11
	};
}
function securityStateFromProbe(info, opts = {}) {
	const permission = permissionInfoFromProbe(info);
	const reasons = [];
	if (info.encryptionRequiresPassword === true && !permission) reasons.push("password");
	if (opts.cdnRequired) reasons.push("cdn");
	if (info.encryptionState === "unknown") reasons.push("permissions-unknown");
	return {
		encryption: {
			state: info.encryptionState,
			requiresPassword: info.encryptionRequiresPassword
		},
		permissions: {
			known: permission?.known ?? false,
			bits: permission?.bits ?? null,
			allAllowed: permission?.allAllowed ?? null,
			openedAs: permission?.openedAs ?? null,
			securityHandlerRevision: permission?.securityHandlerRevision ?? null,
			canUpgradeToOwner: info.encryptionState === "encrypted" && info.pdfOpenedAs !== "owner"
		},
		access: {
			required: reasons.length > 0,
			reasons,
			...reasons.length > 0 && opts.accessEndpoint ? { endpoint: opts.accessEndpoint } : {}
		}
	};
}
//#endregion
//#region src/engine/DocumentRenderService.ts
const CONTINUOUS_RENDER_POLICY = { kind: "continuous" };
/**
* Conform a FULL-PAGE viewport to a render policy — the ONE snap
* implementation, shared by every engine and plugin. Pure and explicit
* by design:
*
* - `continuous` → identity (local/cloud plugin code stays byte-identical).
* - `lattice` → returns a `width`-kind viewport at the smallest ladder
*   width ≥ the requested detail (snap UP: always crisp, CSS downscales
*   for free), capped at the largest width — detail beyond the cap is the
*   tile pyramid's job, by design.
* - `scale`-kind requests convert through the page's width in PDF user
*   units — pass `opts.pageWidth` (the caller has it from the layout);
*   omitting it for a scale viewport under a lattice policy is a
*   programmer error and throws `InvalidArg`.
*/
function snapFullPageViewport(policy, viewport, opts) {
	if (policy.kind === "continuous") return viewport;
	if (policy.fullPage.widths.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "render policy has an empty width ladder");
	const widths = [...policy.fullPage.widths].sort((a, b) => a - b);
	let requested;
	if (viewport.kind === "width") requested = viewport.width;
	else {
		const pageWidth = opts?.pageWidth;
		if (pageWidth === void 0 || pageWidth <= 0) throw new EngineError(EngineErrorCode.InvalidArg, "snapping a scale viewport to a width lattice requires opts.pageWidth");
		requested = (viewport.scale ?? 1) * pageWidth;
	}
	return {
		kind: "width",
		width: widths.find((width) => width >= requested) ?? widths[widths.length - 1]
	};
}
/**
* Conform an annotation-appearance scale to the policy's appearance
* lattice. Same discipline as {@link snapFullPageViewport}: identity on
* continuous (and when the policy carries no appearance block), snap UP,
* cap at the largest point. Callers derive `scale` from the page's
* effective render scale (`snappedWidth / pageWidth`, × devicePixelRatio
* as needed) so composites stay crisp against the page raster.
*/
function snapAppearanceScale(policy, scale) {
	if (policy.kind === "continuous" || policy.appearances === void 0) return scale;
	if (policy.appearances.scales.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "render policy has an empty appearance-scale lattice");
	const scales = [...policy.appearances.scales].sort((a, b) => a - b);
	return scales.find((s) => s >= scale) ?? scales[scales.length - 1];
}
/**
* Conform a tile-pyramid scale to the policy's tile lattice — the third
* member of the `snap<PolicyBlock><Quantity>` family. Identity on
* continuous and while the deployment doesn't advertise a `tiles` block
* (the block is reserved until the deep-zoom vertical ships server-side;
* the tiling client uses its own default pyramid then — see
* plugin-render). Snap UP, cap at the largest scale: per-tile cost is
* constant by construction (`tileSize²` pixels regardless of level), so
* the cap bounds artifact COUNT, not memory.
*/
function snapTileScale(policy, scale) {
	if (policy.kind === "continuous" || policy.tiles === void 0) return scale;
	if (policy.tiles.scales.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "render policy has an empty tile-scale lattice");
	const scales = [...policy.tiles.scales].sort((a, b) => a - b);
	return scales.find((s) => s >= scale) ?? scales[scales.length - 1];
}
//#endregion
//#region src/engine/passwordPrompt.ts
/**
* Derive the high-level password prompt from the raw security state.
*
* The function is pure and total — every `DocumentSecurityState`
* maps to exactly one `PasswordPrompt`. Use this as the SINGLE source
* of truth wherever the SDK exposes a "do you need a password?" flag:
* both `LocalDocumentSecurityService` and `CloudDocumentSecurityService`
* call it, so the contract can never drift.
*
* Mapping rules (top to bottom; first match wins):
*
*   1. encryption.state === 'none'
*      → { state: 'none' }              the document isn't encrypted
*
*   2. !permissions.known
*      → { state: 'required', hint: null }   we haven't probed yet; can't
*                                            tell user-vs-owner; show
*                                            generic prompt
*
*   3. requiresPassword && openedAs === 'none'
*      → { state: 'required', hint: 'user' }   encrypted, anonymous open
*                                              failed → user pwd needed
*
*   4. openedAs === 'owner'
*      → { state: 'none' }              already at the top — nothing more
*                                       to unlock
*
*   5. permissions.canUpgradeToOwner === true
*      → { state: 'optional', hint: 'owner' }   open as user (or as none
*                                               for owner-pwd-only docs)
*                                               but more is available
*
*   6. fallthrough → { state: 'none' }   open and nothing more available
*/
function passwordPromptFromState(state) {
	if (state.encryption.state === "none") return { state: "none" };
	if (!state.permissions.known) return {
		state: "required",
		hint: null
	};
	if (state.encryption.requiresPassword && state.permissions.openedAs === "none") return {
		state: "required",
		hint: "user"
	};
	if (state.permissions.openedAs === "owner") return { state: "none" };
	if (state.permissions.canUpgradeToOwner) return {
		state: "optional",
		hint: "owner"
	};
	return { state: "none" };
}
//#endregion
//#region src/wire/WirePack.ts
/**
* Singleton empty transfer list. Frozen so accidental in-place
* mutation throws in strict mode rather than silently mutating the
* default for future calls.
*/
const EMPTY_TRANSFER = Object.freeze([]);
/**
* Producer-side helper: pair a typed payload with its transfer list.
*
* The `transfer` argument is optional; omitting it (or passing `[]`)
* is the explicit, type-checked way to say "I declare this message
* carries no transferables." That's structurally identical to the
* shape the v2 walker generated for non-binary messages, but here
* the declaration is at construction time, in the producer's own
* code, where the knowledge actually lives.
*
*   wirePack({ kind: 'metadata.read', jobId, docId })
*   wirePack({ kind: 'open.fatMem', jobId, docId, bytes }, [bytes])
*   wirePack({ tag: 'page.render', pixels }, [pixels.buffer])
*/
function wirePack(payload, transfer = EMPTY_TRANSFER) {
	return {
		payload,
		transfer
	};
}
//#endregion
export { ANNOTATION_SUBTYPES, AbortError, AbortablePromise, CONTINUOUS_RENDER_POLICY, DEFAULT_PDF_SAVE_MODE, EMPTY_TRANSFER, EngineError, EngineErrorCode, InvalidScope, MissingIdentity, NO_ANNOTATION_FLAGS, PDF_BITS, PDF_CODE_TO_SUBTYPE, PDF_SUBTYPE_TO_CODE, PdfAnnotationSubtypeCode, PermissionDenied, SEARCH_FOLD_VERSION, SEARCH_REGEX_MAX_LENGTH, SEARCH_SNIPPET_CONTEXT, UNKNOWN_WEAK_ANNOTATION_STATE, advisoryFromPdfBits, appearanceImpactOf, buildSnippet, buildThreads, canonicalSearchQuery, caps, changesAnnotationList, checkAnyCapability, checkCapability, checkCollab, checkSetGroup, classifyRelation, collab, createPageImageHandle, decodeFieldRefKey, decodePdfBits, decodeStableIdKey, deserializeError, encodeFieldRefKey, encodeStableIdKey, expandRawScope, expandedCapabilities, filterMatches, foldOptionsFor, foldText, invalidatesWeakIndexRefs, isAbortError, isValidPageObjectNumber, knownWeakAnnotationState, matchLiteral, matchPageText, matchRegex, materializePdfPermissions, normalizeAnnotationDraft, normalizeAnnotationPatch, normalizeAttachmentFileSource, normalizePdfRect, parseScope, passwordPromptFromState, pdfPermissions, pdfQuadBounds, pdfQuadCorners, pdfQuadFromCorners, pdfRectFromOriginSize, pdfRectHeight, pdfRectSize, pdfRectToOriginSize, pdfRectWidth, permissionInfoFromProbe, permissionInfoWithAdvisory, refKey, resolveBinarySource, revisionTokensEqual, searchContentEpoch, searchRectsForRange, securityStateFromHead, securityStateFromProbe, semanticEqual, serializeError, shiftsExistingAnnotationIndices, snapAppearanceScale, snapFullPageViewport, snapTileScale, sniffBinaryMetadata, subtypeFromCode, toOriginalRange, validateScopeArray, validateSearchQuery, validateSearchRegex, wirePack, wordAt, wordBefore };

//# sourceMappingURL=runtime.js.map